// Implements: F-MCK-002, F-MCK-011
/**
 * The gallery's filters, as the query `GET /mockups` already understands (`q`, `tag`, `favorite`,
 * `scope_kind`, `scope_id`). The server filters; nothing here narrows a loaded page by hand, so
 * "Cargar más" and the filters never disagree about what is in the list.
 *
 * The order (mockup v3 P8, `sort`) travels with the filters, so a change of order starts a list of
 * its own from the first page (the server binds its cursor to the order). It never narrows the list.
 */

import type { GallerySort } from "../../../types/api";

export interface GalleryFilters {
  /** Free text for the server's `q` (name or tag); blank means no search. */
  readonly q: string;
  readonly tag: string | null;
  readonly favorite: boolean;
  /** A scope kind, and its id unless it is the viewer's own personal scope. */
  readonly scope: { readonly kind: string; readonly id: string | null } | null;
  /** The order of the pages; `recent` is the server's own default and is not sent. */
  readonly sort: GallerySort;
}

export const NO_FILTERS: GalleryFilters = { q: "", tag: null, favorite: false, scope: null, sort: "recent" };

/** The filters as a query string; empty values are left out so the server sees only a choice. */
export function filterParams(filters: GalleryFilters): URLSearchParams {
  const params = new URLSearchParams();
  const q = filters.q.trim();
  if (q !== "") params.set("q", q);
  if (filters.tag !== null) params.set("tag", filters.tag);
  if (filters.favorite) params.set("favorite", "true");
  if (filters.scope !== null) {
    params.set("scope_kind", filters.scope.kind);
    if (filters.scope.id !== null) params.set("scope_id", filters.scope.id);
  }
  if (filters.sort !== "recent") params.set("sort", filters.sort);
  return params;
}

/** Whether the list is narrowed at all (a blank search is not a filter, and an order narrows nothing). */
export function hasFilters(filters: GalleryFilters): boolean {
  return filterParams({ ...filters, sort: "recent" }).size > 0;
}

/** The scope alone, as `GET /mockups/counts` takes it: the counts follow the Ámbito only (P8-3). */
export function scopeParams(filters: GalleryFilters): URLSearchParams {
  return filterParams({ ...NO_FILTERS, scope: filters.scope });
}
